/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { ItemAvailability, OrgUnitAvailability, OrgUnitLocation } from '@msdyn365-commerce/retail-proxy';

import { ICrossEntityInventoryNode } from './cross-entity-inventory-types';

/**
 * Maps the location-related fields of an OrgUnitLocation onto a Cross-LE node.
 * @param location - The org unit location returned by the proxy.
 * @returns A partially populated node with no availability fields.
 */
export const mapOrgUnitLocationToCrossEntityNode = (location: OrgUnitLocation | undefined): ICrossEntityInventoryNode => {
    if (!location) {
        return {};
    }

    return {
        orgUnitNumber: location.OrgUnitNumber,
        orgUnitName: location.OrgUnitName,
        dataAreaId: location.DataAreaId,
        channelId: location.ChannelId,
        inventoryLocationId: location.InventoryLocationId,
        inventorySiteId: location.InventorySiteId,
        address: location.Address,
        street: location.Street,
        city: location.City,
        county: location.County ?? location.CountyName,
        state: location.State,
        stateName: location.StateName,
        zip: location.Zip,
        country: location.Country,
        latitude: location.Latitude,
        longitude: location.Longitude,
        distance: location.Distance,
        distanceUnitResultValue: location.DistanceUnitResultValue,
        isPickupLocation: location.IsPickupLocation,
        isShippingLocation: location.IsShippingLocation,
        contacts: location.Contacts
    };
};

/**
 * Selects the item availability that matches the requested product, falling back to the first entry.
 * @param itemAvailabilities - The list of item availabilities on the OrgUnitAvailability.
 * @param productId - The product id being searched for.
 * @returns The matching item availability, if any.
 */
const selectItemAvailability = (itemAvailabilities: ItemAvailability[] | undefined, productId?: number): ItemAvailability | undefined => {
    if (!itemAvailabilities || itemAvailabilities.length === 0) {
        return undefined;
    }

    if (productId !== undefined) {
        const match = itemAvailabilities.find(item => item.ProductId === productId);
        if (match) {
            return match;
        }
    }

    return itemAvailabilities[0];
};

/**
 * Maps a product-scoped OrgUnitAvailability onto a Cross-LE node, including availability fields.
 * @param availability - The org unit availability returned by SearchProductAvailability.
 * @param productId - The product id being searched for (used to select the matching item).
 * @returns A node populated with location and availability fields.
 */
export const mapOrgUnitAvailabilityToCrossEntityNode = (
    availability: OrgUnitAvailability | undefined,
    productId?: number
): ICrossEntityInventoryNode => {
    if (!availability) {
        return {};
    }

    const node = mapOrgUnitLocationToCrossEntityNode(availability.OrgUnitLocation);
    const item = selectItemAvailability(availability.ItemAvailabilities, productId);

    if (item) {
        node.availableQuantity = item.AvailableQuantity;
        node.orderedSum = item.OrderedSum;
        node.physicalReserved = item.PhysicalReserved;
        node.unitOfMeasure = item.UnitOfMeasure;
        node.inventoryUnitOfMeasureDescription = item.InventoryUnitOfMeasureDescription;
    } else {
        // No availability data returned for this node => treat as zero on-hand.
        node.availableQuantity = 0;
    }

    return node;
};
